"use client"

import type React from "react"
import { useState, useCallback, useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import type { Map as LeafletMap } from "leaflet"
import { Trash2, X, Undo2, Redo2, CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"

// Component imports
import TerritorySearch from "./components/TerritorySearch"
import TerritoryInput from "./components/TerritoryInput"
import { Footer } from "./components/Footer"
import AboutUsPopup from "./components/AboutUsPopup"
import ContactUsPopup from "./components/ContactUsPopup"
import PrivacyPolicyPopup from "./components/PrivacyPolicyPopup"
import TermsOfServicePopup from "./components/TermsOfServicePopup"
import { ConfirmViewChangeDialog } from "./components/ConfirmViewChangeDialog"
import { CollapsibleSection } from "./components/CollapsibleSection"
import { MobileNotice } from "./components/MobileNotice"
import MobileMenu from "./components/MobileMenu"
import MobileTerritoryCards from "./components/MobileTerritoryCards"
import HelpPopup from "./components/HelpPopup"
import ExampleUseCases from "./components/ExampleUseCases"
import QuickStartGuide from "./components/QuickStartGuide"

// Utility imports
import { stateAbbreviations } from "./utils/stateMappings"
import { countryAbbreviations } from "./utils/countryMappings"
import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage, type StoredMapData } from "./utils/storageUtils"

// Dynamic imports with ssr: false - VanillaMap uses plain Leaflet (no react-leaflet)
const VanillaMap = dynamic(
  () => import("./components/VanillaMap"),
  { 
    ssr: false,
    loading: () => (
      <div className="h-[600px] w-full rounded-lg bg-gray-100 animate-pulse flex items-center justify-center">
        <div className="text-gray-500">Loading map...</div>
      </div>
    )
  }
)
const ExportOptions = dynamic(
  () => import("./components/ExportOptions"),
  { ssr: false }
)

// Import PapaParse
import Papa from "papaparse"

// Color palettes
const COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#FFA07A",
  "#98D8C8",
  "#F06292",
  "#AED581",
  "#7986CB",
  "#4DB6AC",
  "#9575CD",
  "#FFD54F",
  "#A1887F",
  "#90A4AE",
  "#FFAB91",
  "#DCE775",
  "#F48FB1",
  "#81C784",
  "#64B5F6",
  "#FFB74D",
  "#BA68C8",
]

const COLOR_THEMES = {
  default: COLORS,
  pastel: [
    "#FFB3BA",
    "#BAFFC9",
    "#BAE1FF",
    "#FFFFBA",
    "#FFDFBA",
    "#E0BBE4",
    "#D4F0F0",
    "#FFC6FF",
    "#DAEAF6",
    "#B2DFDB",
  ],
  vibrant: [
    "#FF1744",
    "#00E676",
    "#2979FF",
    "#FFEA00",
    "#FF9100",
    "#D500F9",
    "#00E5FF",
    "#F50057",
    "#1DE9B6",
    "#FFAB00",
  ],
  earthy: [
    "#795548",
    "#827717",
    "#004D40",
    "#880E4F",
    "#3E2723",
    "#1B5E20",
    "#BF360C",
    "#006064",
    "#4A148C",
    "#FF6F00",
  ],
}

// Types
interface Territory {
  id: number
  name: string
  territory?: string
  territories: string[]
  color: string
}

// Sample data
const sampleData: Territory[] = [
  { id: 1, name: "John Smith", territories: ["ME", "NH", "VT"], color: COLORS[0] },
  { id: 2, name: "Emily Johnson", territories: ["MD", "DE", "VA"], color: COLORS[1] },
  { id: 3, name: "Michael Brown", territories: ["OH", "MI", "IN"], color: COLORS[2] },
  { id: 4, name: "Sarah Davis", territories: ["TX", "OK", "NM"], color: COLORS[3] },
]

const worldSampleData: Territory[] = [
  { id: 1, name: "Maria Rodriguez", territories: ["MEX", "COL", "ARG", "CHL"], color: COLORS[0] },
  { id: 2, name: "James Wilson", territories: ["GBR", "FRA", "DEU", "ESP"], color: COLORS[1] },
  { id: 3, name: "Yuki Tanaka", territories: ["JPN", "KOR", "TWN", "SGP"], color: COLORS[2] },
  { id: 4, name: "Sarah Thompson", territories: ["CAN", "USA", "AUS", "NZL"], color: COLORS[3] },
]

// Logo component
const Logo = () => (
  <div aria-label="Coverage Maps Logo" role="img">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50" aria-hidden="true">
      <title>Coverage Maps Logo</title>
      <desc>A stylized globe representing the Coverage Maps application</desc>
      <defs>
        <linearGradient id="globeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#60a5fa", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "#3b82f6", stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill="white" stroke="#3b82f6" strokeWidth="2" />
      <circle cx="50" cy="50" r="35" fill="url(#globeGradient)" />
      <path d="M15 50 Q30 35 50 35 Q70 35 85 50" fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" />
      <path d="M15 50 Q30 65 50 65 Q70 65 85 50" fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" />
      <path d="M20 50 Q32 42 50 42 Q68 42 80 50" fill="none" stroke="white" strokeWidth="1" opacity="0.4" />
      <path d="M20 50 Q32 58 50 58 Q68 58 80 50" fill="none" stroke="white" strokeWidth="1" opacity="0.4" />
      <path d="M50 15 Q35 30 35 50 Q35 70 50 85" fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" />
      <path d="M50 15 Q65 30 65 50 Q65 70 50 85" fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" />
      <path d="M50 20 Q42 32 42 50 Q42 68 50 80" fill="none" stroke="white" strokeWidth="1" opacity="0.4" />
      <path d="M50 20 Q58 32 58 50 Q58 68 50 80" fill="none" stroke="white" strokeWidth="1" opacity="0.4" />
    </svg>
  </div>
)

// Action type for undo/redo
interface Action {
  type: "add" | "edit" | "delete" | "map_move" | "assign_territory"
  territory?: Territory
  previousState?: Partial<Territory>
  mapState?: {
    center: [number, number]
    zoom: number
  }
  assignment?: {
    region: string
    territoryId: number | null
  }
  previousTerritories?: Territory[]
}

export default function ClientPage() {
  // Core state
  const [territories, setTerritories] = useState<Territory[]>(sampleData)
  const [mapMode, setMapMode] = useState<"us" | "world">("us")
  const [mapTitle, setMapTitle] = useState("Coverage Maps Title")

  // UI state
  const [isMobile, setIsMobile] = useState(false)
  const [colorTheme, setColorTheme] = useState<keyof typeof COLOR_THEMES>("default")
  const [isDefaultData, setIsDefaultData] = useState(true)
  const [showTerritoryColumn, setShowTerritoryColumn] = useState(false)
  const [showFreeBanner, setShowFreeBanner] = useState(true)

  // Search and filtering
  const [filteredTerritories, setFilteredTerritories] = useState<Territory[]>(territories)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeFilter, setActiveFilter] = useState("")
  const [highlightedRegions, setHighlightedRegions] = useState<string[]>([])
  const [allRegions, setAllRegions] = useState<string[]>([])
  const [currentFilter, setCurrentFilter] = useState("all")
  const [selectedTerritory, setSelectedTerritory] = useState<number | null>(null)
  const [searchHighlightedRegions, setSearchHighlightedRegions] = useState<string[]>([])

  // Map state
  const [mapInstance, setMapInstance] = useState<LeafletMap | null>(null)

  // Dialogs and popups
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isTermsOfServiceOpen, setIsTermsOfServiceOpen] = useState(false)
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false)
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false)
  const [isContactUsOpen, setIsContactUsOpen] = useState(false)
  const [showViewChangeConfirm, setShowViewChangeConfirm] = useState(false)
  const [pendingViewChange, setPendingViewChange] = useState<"us" | "world" | null>(null)

  // History
  const [undoStack, setUndoStack] = useState<Action[]>([])
  const [redoStack, setRedoStack] = useState<Action[]>([])

  // Storage
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false)

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  // Add action to undo stack
  const pushToUndoStack = useCallback((action: Action) => {
    setUndoStack((prevStack) => [...prevStack, action])
    setRedoStack([])
  }, [])

  // Check if on mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Set available regions based on map mode
  useEffect(() => {
    const regions = mapMode === "us" ? Object.values(stateAbbreviations) : Object.values(countryAbbreviations)
    setAllRegions(regions)
  }, [mapMode])

  // Load data from local storage
  useEffect(() => {
    const storedData = loadFromLocalStorage()
    if (storedData && !hasLoadedInitialData) {
      setTerritories(storedData.territories)
      setFilteredTerritories(storedData.territories)
      setMapMode(storedData.mapMode)
      setMapTitle(storedData.mapTitle)
      setIsDefaultData(false)
      setLastSaved(new Date(storedData.lastModified))
      setHasLoadedInitialData(true)
    }
  }, [hasLoadedInitialData])

  // Save data to local storage when territories change
  useEffect(() => {
    if (hasLoadedInitialData) {
      const now = Date.now()
      saveToLocalStorage({
        territories,
        mapMode,
        mapTitle,
        lastModified: now,
      })
      setLastSaved(new Date(now))
    }
  }, [territories, mapMode, mapTitle, hasLoadedInitialData])

  // Import data handler
  const handleImportData = useCallback(
    (data: StoredMapData) => {
      setTerritories(data.territories)
      setFilteredTerritories(data.territories)
      setMapMode(data.mapMode)
      setMapTitle(data.mapTitle)
      setIsDefaultData(false)
      setLastSaved(new Date(data.lastModified))

      // Add this code to update the map view based on the loaded map mode
      setTimeout(() => {
        if (mapInstance) {
          if (data.mapMode === "world") {
            mapInstance.setView([20, 0], 2)
          } else {
            mapInstance.setView([37.8, -96], 4)
          }
          mapInstance.invalidateSize()
        }
      }, 100)
    },
    [mapInstance],
  )

  // Map mode change handler
  const handleModeChange = useCallback(() => {
    // Store the target mode we want to switch to
    const targetMode = mapMode === "us" ? "world" : "us"
    setPendingViewChange(targetMode)

    // Check if current territories differ from the default sample data
    const currentSampleData = mapMode === "us" ? sampleData : worldSampleData
    const hasEdits = JSON.stringify(territories) !== JSON.stringify(currentSampleData)

    if (hasEdits && !isDefaultData) {
      // Show confirmation dialog if there are unsaved changes
      setShowViewChangeConfirm(true)
    } else {
      // Proceed with view change if no unsaved changes
      const newMode = targetMode

      // Update the data
      setTerritories(newMode === "us" ? sampleData : worldSampleData)
      setFilteredTerritories(newMode === "us" ? sampleData : worldSampleData)
      setIsDefaultData(true)

      // Update the map mode
      setMapMode(newMode)

      // Reset the pending view change
      setPendingViewChange(null)
      setShowViewChangeConfirm(false)
    }
  }, [territories, mapMode, isDefaultData])

  // Perform view change after confirmation
  const performViewChange = useCallback(() => {
    if (!pendingViewChange) return

    const newMode = pendingViewChange

    // Update the data
    setTerritories(newMode === "us" ? sampleData : worldSampleData)
    setFilteredTerritories(newMode === "us" ? sampleData : worldSampleData)
    setIsDefaultData(true)

    // Update the map mode
    setMapMode(newMode)

    // Reset the pending view change
    setPendingViewChange(null)
    setShowViewChangeConfirm(false)
  }, [pendingViewChange])

  // Cancel view change
  const cancelViewChange = useCallback(() => {
    setPendingViewChange(null)
    setShowViewChangeConfirm(false)
  }, [])

  // File upload handler
  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const csvData = e.target?.result as string
            Papa.parse(csvData, {
              header: true,
              complete: (results) => {
                const data = results.data as any[]
                if (data.length === 0) return

                // Process CSV data
                const newTerritories = data.reduce((acc: Territory[], row: any, index: number) => {
                  const name = row.name || row.Name || ""
                  const territory = row.territory || row.Territory || ""
                  const geography = row.geography || row.Geography || row.territories || row.Territories || ""

                  if (name) {
                    const territories = typeof geography === "string" ? geography.split(",").map((t) => t.trim()) : []

                    acc.push({
                      id: index + 1,
                      name,
                      territory,
                      territories,
                      color: COLOR_THEMES[colorTheme][index % COLOR_THEMES[colorTheme].length],
                    })
                  }
                  return acc
                }, [])

                if (newTerritories.length > 0) {
                  setTerritories(newTerritories)
                  setFilteredTerritories(newTerritories)
                  setIsDefaultData(false)
                }
              },
            })
          } catch (error) {
            console.error("Error parsing CSV:", error)
            alert("Error parsing CSV file. Please check the format.")
          }
        }
        reader.readAsText(file)
      }
    },
    [colorTheme],
  )

  // Add new territory row
  const handleAddRow = useCallback(
    (name = "") => {
      const newRow: Territory = {
        id: territories.length + 1,
        name: name || "",
        territories: [],
        color: COLOR_THEMES[colorTheme][territories.length % COLOR_THEMES[colorTheme].length],
      }
      pushToUndoStack({ type: "add", territory: newRow })
      setTerritories((prev) => [...prev, newRow])
      setFilteredTerritories((prev) => [...prev, newRow])
      setIsDefaultData(false)
      return newRow.id
    },
    [colorTheme, territories.length, pushToUndoStack],
  )

  // Update territory field
  const handleTerritoryChange = useCallback(
    (id: number, field: keyof Territory, value: string | string[]) => {
      setTerritories((prev) => {
        const updatedTerritories = prev.map((territory) => {
          if (territory.id === id) {
            const previousState = { [field]: territory[field] }
            const updatedTerritory = {
              ...territory,
              [field]: field === "territories" ? (Array.isArray(value) ? value : [value]) : value,
            }
            pushToUndoStack({ type: "edit", territory: updatedTerritory, previousState })
            return updatedTerritory
          }
          return territory
        })
        setFilteredTerritories(updatedTerritories)
        return updatedTerritories
      })
      setIsDefaultData(false)
    },
    [pushToUndoStack],
  )

  // Delete territory
  const handleDeleteTerritory = useCallback(
    (id: number) => {
      setTerritories((prev) => {
        const territoryToDelete = prev.find((t) => t.id === id)
        if (territoryToDelete) {
          pushToUndoStack({ type: "delete", territory: territoryToDelete })
        }
        const updatedTerritories = prev.filter((territory) => territory.id !== id)
        setFilteredTerritories(updatedTerritories)
        return updatedTerritories
      })
      setIsDefaultData(false)
    },
    [pushToUndoStack],
  )

  // Change color theme
  const handleColorThemeChange = useCallback((theme: keyof typeof COLOR_THEMES) => {
    setColorTheme(theme)
    setTerritories((prev) =>
      prev.map((territory, index) => ({
        ...territory,
        color: COLOR_THEMES[theme][index % COLOR_THEMES[theme].length],
      })),
    )
    setFilteredTerritories((prev) =>
      prev.map((territory, index) => ({
        ...territory,
        color: COLOR_THEMES[theme][index % COLOR_THEMES[theme].length],
      })),
    )
  }, [])

  // Clear table
  const handleClearTable = useCallback(() => {
    setTerritories([])
    setFilteredTerritories([])
    setIsDefaultData(false)
    clearLocalStorage()
  }, [])

  // Reset to default view
  const resetToDefaultView = useCallback(() => {
    setTerritories(sampleData)
    setFilteredTerritories(sampleData)
    setMapMode("us")
    setMapTitle("Coverage Maps Title")
    setIsDefaultData(true)
  }, [])

  // Assign region to territory
  const handleAssignRegion = useCallback(
    (region: string, territoryId: number | null) => {
      setTerritories((prev) => {
        const previousTerritories = [...prev]
        const updatedTerritories = prev.map((territory) => {
          if (territory.id === territoryId) {
            const newTerritories = [...new Set([...territory.territories, region])].sort()
            return {
              ...territory,
              territories: newTerritories,
            }
          }
          if (territory.territories.includes(region)) {
            return {
              ...territory,
              territories: territory.territories.filter((t) => t !== region),
            }
          }
          return territory
        })
        pushToUndoStack({
          type: "assign_territory",
          assignment: { region, territoryId },
          previousTerritories: previousTerritories,
        })
        setFilteredTerritories(updatedTerritories)
        return updatedTerritories
      })
      setIsDefaultData(false)
    },
    [pushToUndoStack],
  )

  // Filter territories based on search term
  useEffect(() => {
    const filtered = territories.filter((territory) => {
      const matchesSearch =
        territory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        territory.territories.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))

      return matchesSearch
    })

    setFilteredTerritories(filtered)
  }, [searchTerm, territories])

  // Map ready handler
  const handleMapReady = useCallback((map: LeafletMap) => {
    setMapInstance(map)
  }, [])

  // Undo handler
  const handleUndo = useCallback(() => {
    if (undoStack.length > 0) {
      const lastAction = undoStack[undoStack.length - 1]
      setUndoStack((prevStack) => prevStack.slice(0, -1))
      setRedoStack((prevStack) => [...prevStack, lastAction])

      switch (lastAction.type) {
        case "add":
        case "edit":
        case "delete":
          setTerritories((prevTerritories) => {
            let newTerritories
            if (lastAction.type === "add") {
              newTerritories = prevTerritories.filter((t) => t.id !== lastAction.territory!.id)
            } else if (lastAction.type === "edit") {
              newTerritories = prevTerritories.map((t) =>
                t.id === lastAction.territory!.id ? { ...t, ...lastAction.previousState } : t,
              )
            } else {
              newTerritories = [...prevTerritories, lastAction.territory!]
            }
            setFilteredTerritories(newTerritories)
            return newTerritories
          })
          break
        case "map_move":
          if (lastAction.mapState && mapInstance) {
            try {
              mapInstance.setView(lastAction.mapState.center, lastAction.mapState.zoom)
            } catch (error) {
              console.error("Error setting map view:", error)
            }
          }
          break
        case "assign_territory":
          if (lastAction.previousTerritories) {
            setTerritories(lastAction.previousTerritories)
            setFilteredTerritories(lastAction.previousTerritories)
          }
          break
      }
    }
  }, [undoStack, mapInstance])

  // Redo handler
  const handleRedo = useCallback(() => {
    if (redoStack.length > 0) {
      const nextAction = redoStack[redoStack.length - 1]
      setRedoStack((prevStack) => prevStack.slice(0, -1))
      setUndoStack((prevStack) => [...prevStack, nextAction])

      switch (nextAction.type) {
        case "add":
        case "edit":
        case "delete":
          setTerritories((prevTerritories) => {
            let newTerritories
            if (nextAction.type === "add") {
              newTerritories = [...prevTerritories, nextAction.territory!]
            } else if (nextAction.type === "edit") {
              newTerritories = prevTerritories.map((t) =>
                t.id === nextAction.territory!.id ? nextAction.territory! : t,
              )
            } else {
              newTerritories = prevTerritories.filter((t) => t.id !== nextAction.territory!.id)
            }
            setFilteredTerritories(newTerritories)
            return newTerritories
          })
          break
        case "map_move":
          if (nextAction.mapState && mapInstance) {
            try {
              mapInstance.setView(nextAction.mapState.center, nextAction.mapState.zoom)
            } catch (error) {
              console.error("Error setting map view:", error)
            }
          }
          break
        case "assign_territory":
          if (nextAction.assignment) {
            const { region, territoryId } = nextAction.assignment
            setTerritories((prevTerritories) => {
              const updatedTerritories = prevTerritories.map((territory) => {
                if (territory.id === territoryId) {
                  return {
                    ...territory,
                    territories: [...new Set([...territory.territories, region])].sort(),
                  }
                }
                if (territory.territories.includes(region)) {
                  return {
                    ...territory,
                    territories: territory.territories.filter((t) => t !== region),
                  }
                }
                return territory
              })
              setFilteredTerritories(updatedTerritories)
              return updatedTerritories
            })
          }
          break
      }
    }
  }, [redoStack, mapInstance])

  // Map move end handler
  const handleMapMoveEnd = useCallback(
    (newState: { center: [number, number]; zoom: number }) => {
      pushToUndoStack({
        type: "map_move",
        mapState: newState,
      })
    },
    [pushToUndoStack],
  )

  return (
    <div className="min-h-screen flex flex-col">
      <MobileNotice />
      <header className="sr-only">
        <h1>Coverage Maps - Territory Mapping Tool</h1>
        <p>Create and manage territory maps for sales, service areas, and franchise territories</p>
      </header>
      <main className="container mx-auto w-full space-y-8 pt-4 sm:pt-6 md:pt-8 lg:pt-10 flex-1">
        <div className="header-container flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-8">
          <div
            className="flex items-center gap-2 group mb-2 sm:mb-0"
            onClick={resetToDefaultView}
            aria-label="Reset to default view"
          >
            <Logo />
            <div className="flex flex-col items-start">
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-700 dark:text-blue-300 cursor-pointer">
                Coverage Maps
              </h1>
              <p className="text-sm text-slate-700 dark:text-slate-300">Create and Export Custom Territory Maps</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <MobileMenu onHelpOpen={setIsHelpOpen} />
          </div>
        </div>

        {/* Free to use banner */}
        {showFreeBanner && (
          <div className="free-banner mb-4 relative">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-green-800 font-medium">
                <span className="font-bold">Free to use</span> - No login, sign up, or account required
              </p>
            </div>
            <button
              onClick={() => setShowFreeBanner(false)}
              className="absolute top-0 right-2 h-full flex items-center text-green-700 hover:text-green-900 transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* SEO-optimized content section with collapsible functionality */}
        <CollapsibleSection
          title="Create and Export Professional Territory and Coverage Maps"
          className="mb-6 shadow-sm border-slate-200/50"
          mobileBreakpoint={768}
          defaultExpanded={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
            <div>
              <p className="mb-2">
                <strong>Coverage Maps</strong> is a free online tool that helps you create professional territory maps
                without requiring any login, sign up, or account creation.
              </p>
              <p className="mb-2">
                Whether you need to visualize your sales territories, service areas, or franchise regions, our
                easy-to-use tool makes the process simple and efficient.
              </p>
              <p className="font-bold mb-2">Export your maps as PNG, PDF, or CSV - completely free.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Key Features:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Interactive US and worldwide territory mapping</li>
                <li>Multiple export options (PNG, PDF, CSV)</li>
                <li>Color-coded region assignment</li>
                <li>No registration or login required</li>
                <li>100% free to use</li>
              </ul>
            </div>
          </div>
        </CollapsibleSection>

        <QuickStartGuide />

        {/* Territory Data Section - Adding better visual delineation */}
        <CollapsibleSection
          title="Territory Data"
          className="mb-8 shadow-sm border-slate-200/50"
          mobileBreakpoint={768}
          defaultExpanded={true}
        >
          <div className="card-container">
            {/* Controls Section - Better organized with visual separation */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex flex-col gap-4">
                {/* Primary actions row */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm">
                      <input
                        type="checkbox"
                        checked={showTerritoryColumn}
                        onChange={(e) => setShowTerritoryColumn(e.target.checked)}
                        className="form-checkbox h-4 w-4 text-blue-600"
                      />
                      <span>Territory</span>
                    </label>
                  </div>

                  {/* Primary actions - always visible and important */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition-colors shadow-sm"
                      aria-label="Upload CSV file"
                    >
                      Upload CSV
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      aria-label="Upload CSV file input"
                    />
                  </div>
                </div>

                {/* Secondary controls row */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {/* History controls */}
                  <div className="flex items-center gap-2">
                    {undoStack.length > 0 && (
                      <Button
                        onClick={handleUndo}
                        size="sm"
                        variant="outline"
                        className="bg-white border border-gray-300 hover:bg-gray-50 shadow-sm"
                        aria-label="Undo"
                      >
                        <Undo2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Undo</span>
                      </Button>
                    )}
                    {redoStack.length > 0 && (
                      <Button
                        onClick={handleRedo}
                        size="sm"
                        variant="outline"
                        className="bg-white border border-gray-300 hover:bg-gray-50 shadow-sm"
                        aria-label="Redo"
                      >
                        <Redo2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Redo</span>
                      </Button>
                    )}
                  </div>

                  {/* Secondary actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={colorTheme}
                      onChange={(e) => handleColorThemeChange(e.target.value as keyof typeof COLOR_THEMES)}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="default">Default Colors</option>
                      <option value="pastel">Pastel Colors</option>
                      <option value="vibrant">Vibrant Colors</option>
                      <option value="earthy">Earthy Colors</option>
                    </select>

                    <button
                      onClick={handleClearTable}
                      className="px-4 py-2 bg-red-400 text-white text-sm font-medium rounded-md hover:bg-red-500 transition-colors shadow-sm"
                    >
                      Clear Table
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Table/Cards Content - Same as before */}
            <div className="p-4 bg-white">
              {/* Desktop Table View - Hidden on Mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 w-1/3">Name</th>
                      {showTerritoryColumn && <th className="text-left p-2 w-1/6">Territory</th>}
                      <th className="text-left p-2 w-1/2">Geography</th>
                      <th className="text-left p-2 w-[30px]">Color</th>
                      <th className="text-left p-2 w-[30px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTerritories.map((territory) => (
                      <tr key={territory.id} className="border-b hover:bg-gray-50 cursor-pointer">
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-sm flex-shrink-0"
                              style={{ backgroundColor: territory.color }}
                            />
                            <input
                              type="text"
                              value={territory.name}
                              onChange={(e) => handleTerritoryChange(territory.id, "name", e.target.value)}
                              className="w-full p-1 border rounded bg-transparent"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </td>
                        {showTerritoryColumn && (
                          <td className="p-2">
                            <input
                              type="text"
                              value={territory.territory}
                              onChange={(e) => handleTerritoryChange(territory.id, "territory", e.target.value)}
                              className="w-full p-1 border rounded bg-transparent"
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Enter territory name"
                            />
                          </td>
                        )}
                        <td className="p-2">
                          <TerritoryInput
                            value={territory.territories}
                            onChange={(newTerritories) =>
                              handleTerritoryChange(territory.id, "territories", newTerritories)
                            }
                            mapMode={mapMode}
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="color"
                            value={territory.color}
                            onChange={(e) => handleTerritoryChange(territory.id, "color", e.target.value)}
                            className="w-7 h-7 p-0 border rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="p-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteTerritory(territory.id)
                            }}
                            className="p-0.5 rounded-full hover:bg-red-100 transition-colors group"
                          >
                            <Trash2 className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View - Only visible on mobile */}
              <div className="md:hidden">
                <MobileTerritoryCards
                  territories={filteredTerritories}
                  onTerritoryChange={handleTerritoryChange}
                  onDeleteTerritory={handleDeleteTerritory}
                  mapMode={mapMode}
                  showTerritoryColumn={showTerritoryColumn}
                />
              </div>
            </div>

            {/* Search Section - With visual separation */}
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <TerritorySearch
                territories={territories}
                onFilteredTerritoriesChange={setFilteredTerritories}
                mapMode={mapMode}
                allRegions={allRegions}
                onHighlightRegions={setHighlightedRegions}
                onSearchHighlightRegions={setSearchHighlightedRegions}
                onAssignRegion={handleAssignRegion}
                onFilterChange={setCurrentFilter}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Map Section - With better visual delineation and improved title/export layout */}
        <section
          aria-labelledby="map-heading"
          className="card-container border-2 border-gray-200 rounded-xl overflow-hidden"
        >
          {/* Map Header with Title and Export */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <h2 id="map-heading" className="text-lg font-semibold text-gray-800">
                Interactive Territory Map
              </h2>

          {/* Export button moved to header section */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <ExportOptions map={mapInstance} mapTitle={mapTitle} className="shrink-0" />
          </div>
            </div>
          </div>

          {/* Map Title Input - Separated from export button */}
          <div className="p-4 bg-white border-b border-gray-200">
            <div className="flex flex-col items-center gap-1 max-w-md mx-auto">
              <input
                type="text"
                value={mapTitle}
                onChange={(e) => setMapTitle(e.target.value)}
                className="text-xl font-semibold w-full px-3 py-2 text-center border border-gray-300 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none placeholder-gray-400"
                placeholder="Enter map title"
                aria-label="Map title"
                maxLength={80}
              />
              <div className="text-xs text-gray-500">{mapTitle.length}/80 characters</div>
              {mapTitle && (
                <button
                  onClick={() => setMapTitle("")}
                  className="text-gray-400 hover:text-gray-600 mt-1"
                  aria-label="Clear map title"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Map Container */}
          <div className="p-4 bg-white" ref={mapRef}>
            <VanillaMap
              territories={territories}
              onTerritoriesChange={setTerritories}
              mapMode={mapMode}
              onModeChange={handleModeChange}
              selectedTerritory={selectedTerritory}
              onTerritorySelect={setSelectedTerritory}
              isMobile={isMobile}
              highlightedRegions={highlightedRegions}
              searchHighlightedRegions={searchHighlightedRegions}
              allRegions={allRegions}
              handleAssignRegion={handleAssignRegion}
              onAddTerritory={(name) => handleAddRow(name)}
              showAssigned={currentFilter !== "unassigned"}
              showTerritoryColumn={showTerritoryColumn}
              mapTitle={mapTitle}
              onMapReady={handleMapReady}
              isDimmed={isHelpOpen}
              onUndo={handleUndo}
              onRedo={handleRedo}
              setTerritories={setTerritories}
              isDefaultData={isDefaultData}
            />
          </div>
        </section>

        <HelpPopup onOpenChange={setIsHelpOpen} isOpen={isHelpOpen} />
        <AboutUsPopup isOpen={isAboutUsOpen} onClose={() => setIsAboutUsOpen(false)} />
        <ContactUsPopup isOpen={isContactUsOpen} onClose={() => setIsContactUsOpen(false)} />
        <PrivacyPolicyPopup isOpen={isPrivacyPolicyOpen} onClose={() => setIsPrivacyPolicyOpen(false)} />
        <TermsOfServicePopup isOpen={isTermsOfServiceOpen} onClose={() => setIsTermsOfServiceOpen(false)} />
        <ConfirmViewChangeDialog
          isOpen={showViewChangeConfirm}
          onClose={cancelViewChange}
          onConfirm={performViewChange}
          currentMode={mapMode}
          hasUnsavedChanges={!isDefaultData}
        />
        <ExampleUseCases />
      </main>
      <Footer
        onTermsClick={() => setIsTermsOfServiceOpen(true)}
        onPrivacyClick={() => setIsPrivacyPolicyOpen(true)}
        onAboutClick={() => setIsAboutUsOpen(true)}
        onContactClick={() => setIsContactUsOpen(true)}
        currentData={{
          territories,
          mapMode,
          mapTitle,
          lastModified: lastSaved?.getTime() || Date.now(),
        }}
        onImport={handleImportData}
      />
    </div>
  )
}
